<script lang="ts">
    import { onMount } from "svelte";
    import { get } from "svelte/store";
    import { meta, router } from "tinro";

    const route = meta();

    import { sendGAEvent } from "../lib/utilities/ga.js";

    import { selectedFormat } from "../lib/data/stores.js";
    import { databases, filteredDatabases, isLoadingDatabases } from "../lib/data/databases.js";
    import { formats } from "../lib/data/formats.js";

    import FilterBar from "../lib/components/FilterBar.svelte";
    import Select from "../lib/components/Select.svelte";
    import Button from "../lib/components/Button.svelte";
    import SearchSummary from "../lib/components/SearchSummary.svelte";
    import DatabaseList from "../lib/components/DatabaseList.svelte";
    import TextBlock from "../lib/components/TextBlock.svelte";
    import NoResults from "../lib/components/NoResults.svelte";
    import Row from "../lib/components/Row.svelte";
    import Spinner from "../lib/components/Spinner.svelte";

    export let content = "";

    let databasesNotFound = false;
    let showSummary = false;
    let mounted = false;

    onMount(async () => {
        const selected = router.location.query.get("selectedFormat");
        console.log("selected: ", selected);

        if (selected) {
            if (typeof selected === "string") {
                selectedFormat.set(decodeURIComponent(selected));
            }
        } else {
            selectedFormat.set("");
        }

        // Only subscribe once, then use get(...) in filterByFormat to avoid double events
        isLoadingDatabases.subscribe(async (isLoading) => {
            if (!isLoading) {
                const allDBs = get(databases);
                filteredDatabases.set(allDBs);
                filterByFormat();
            }
        });

        mounted = true;
    });

    // Keep this subscription so filteredDatabases is at least initialized
    databases.subscribe((dbs) => {
        filteredDatabases.set(dbs);
    });

    // Reactive block: once mounted, reflect $selectedFormat in query params
    $: {
        if (mounted) {
            filterByFormat();
            if ($selectedFormat) {
                console.log("selectedFormat reactive: ", $selectedFormat);
                router.location.query.set("selectedFormat", encodeURIComponent($selectedFormat));
            } else {
                router.location.query.delete("selectedFormat");
            }
        }
    }

    function filterByFormat() {
        const allDBs = get(databases);
        filteredDatabases.set(allDBs);
        databasesNotFound = false;
        showSummary = false;

        const currentFormat = get(selectedFormat);
        if (currentFormat) {
            filteredDatabases.update((currentDatabases) => {
                const filtered = currentDatabases.filter((database) => {
                    return (
                        database.types &&
                        database.types.some((type) => type.name === currentFormat)
                    );
                });

                if (filtered.length > 0) {
                    showSummary = true;
                    databasesNotFound = false;
                    return filtered;
                } else {
                    showSummary = true;
                    databasesNotFound = true;
                    return [];
                }
            });

            console.log("filtered: ", filteredDatabases);

            sendGAEvent(
                "ByFormat",
                "format_filter",
                currentFormat,
                route.match
            );
        } else {
            filteredDatabases.set(allDBs);
            databasesNotFound = false;
        }

        console.log(databasesNotFound);
    }

    function clearFilter() {
        showSummary = false;
        selectedFormat.set("");
        router.location.query.delete("selectedFormat");
        filteredDatabases.set(get(databases));

        filterByFormat();

        sendGAEvent(
            "ByFormat",
            "clear_filter",
            "All filters cleared",
            route.match
        );
    }
</script>

<FilterBar parent_id="by-format" showCopyUrl={true}>
    <TextBlock
            classes="mb-3"
            title={content.label}
            text={content.heading}
    />
    <form id="databaseFilter" class="form-inline d-flex flex-nowrap">
        <Select
                options={formats}
                id="filterControl"
                bind:value={$selectedFormat}
                optionsname="Format"
                on:change={filterByFormat}
        />
        <Button on:click={clearFilter} label="clear" classes="btn btn-info small" />
    </form>
</FilterBar>

{#if showSummary}
    {#if $isLoadingDatabases}
        <Spinner />
    {:else}
        <Row>
            <SearchSummary
                    on:copyToClipboard
                    count={$filteredDatabases.length}
                    searchTerm={$selectedFormat}
            />
        </Row>
    {/if}
{/if}

{#if $filteredDatabases.length > 0}
    <DatabaseList
            on:openDialog
            bind:databases={$filteredDatabases}
    />
{:else}
    <NoResults />
{/if}

<style>
</style>
